import { isFaireDirectVariant } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/AppSignUpModalVariant";
import { useEmailState } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/state/useEmailState";
import { useHandleModalState } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/state/useHandleModalState";
import { useStoreOpeningDateState } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/state/useStoreOpeningDateState";
import { useTrackEmailEntryAndSubmit } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/state/useTrackEmailEntryAndSubmit";
import {
  buildFaireDirectParams,
  buildFaireDirectSignUpParamsForVisitor,
} from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/utils/paramBuilders";
import { ssoCache } from "@faire/deprecated-retailer-visitor-shared/features/SignUp/SSORequestData";
import { showSSOButtons } from "@faire/deprecated-retailer-visitor-shared/lib/ssoUtils";
import { useSSOPasswordStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOProviderOptionsStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOProviderOptionsStore";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useFaireDirectBrand } from "@faire/faire-direct/lib/hooks/useFaireDirectBrand/useFaireDirectBrand";
import { useSettingFaireDirectSignupForVisitorXP } from "@faire/faire-direct/settings/useSettingFaireDirectCreditForVisitor";
import userLoginData from "@faire/web-api--source/endpoints/www/api/user/login-data/post";
import saveIncompleteSignupAttempt from "@faire/web-api--source/endpoints/www/api/user/retailer-signup-attempt/post";
import { trackSignupReportExistingEmail } from "@faire/web-api--source/events/signup/actionCategoryOther/reportExistingEmail";
import { trackSignupReportExistingSso } from "@faire/web-api--source/events/signup/actionCategoryOther/reportExistingSso";
import { ILoginDataRequest } from "@faire/web-api--source/indigofair/data/ILoginDataRequest";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { route as videoPageRoute } from "@faire/web-api--source/routes/www/video/videoToken";
import signUpPersonal, {
  QueryParameters as SignUpQueryParameters,
} from "@faire/web-api--source/routes/www/welcome/r/personal";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { pathMatchesRoute } from "@faire/web/common/routes/RouteMatcher";
import { removeLocaleFromUrl } from "@faire/web/common/routing/util";
import { trackEvent } from "@faire/web/common/trackEvent";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import qs from "qs";
import { useCallback, useMemo } from "react";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";

export const useAppSignUpState = () => {
  const location = useLocation();
  const history = useHistory();
  const { setProviders } = useSSOProviderOptionsStore();
  const { setUserHasPassword } = useSSOPasswordStore();
  const { setPromptSSO, isActive } = useSSOStore();
  const { faireDirectBrand, faireDirectBrandToken, faireDirectBrandAlias } =
    useFaireDirectBrand();

  const isFaireDirectCreditForVisitorPhase2 =
    useSettingFaireDirectSignupForVisitorXP();

  const { markUserEntersEmail, trackTimeBetweenEmailEntryAndSubmit } =
    useTrackEmailEntryAndSubmit();

  const signUpParams = useQueryParams(SignUpQueryParameters.parse);

  const { modalVariant, handleModalClose, recordModalSignUpActivity } =
    useHandleModalState();
  const isFaireDirectSignUp = isFaireDirectVariant(modalVariant);

  const {
    email,
    getIsEmailAvailable,
    setEmailValidationErrors,
    setSomethingWentWrongEmailError,
  } = useEmailState();

  const { storeOpeningDate } = useStoreOpeningDateState();

  const dismissible = useMemo(
    () =>
      !signUpParams.livestream && !pathMatchesRoute(location, videoPageRoute),
    [location, signUpParams.livestream]
  );

  const saveIncompleteSignup = useCallback(async () => {
    const payload = ISignUpRequest.build({
      user: IUser.build({
        email_address: email.value,
      }),
    });
    try {
      await saveIncompleteSignupAttempt(payload);
    } catch (error) {
      // error handling be BE; background network task, user shouldn't know
      // this request failed
    }
  }, [email.value]);

  const signupWithAvailableEmail = useCallback(
    async (queryParamsOverride?: Partial<SignUpQueryParameters>) => {
      const faireDirectParams = isFaireDirectCreditForVisitorPhase2
        ? buildFaireDirectSignUpParamsForVisitor(signUpParams, modalVariant)
        : buildFaireDirectParams(
            signUpParams,
            modalVariant,
            faireDirectBrandToken,
            faireDirectBrand?.name,
            faireDirectBrandAlias
          );

      const query = {
        ...signUpParams,
        email: email.value.trim(),
        source: removeLocaleFromUrl(location.pathname),
        storeOpeningDate:
          storeOpeningDate.value && storeOpeningDate.value > 0
            ? `${storeOpeningDate.value}`
            : undefined,
        ...faireDirectParams,
        ...queryParamsOverride,
      };

      const redirectLocation: string = signUpPersonal(query);

      void trackEvent(`sign-up-page/modal/submit-email/success`);
      recordModalSignUpActivity();
      await saveIncompleteSignup();
      if (isActive) {
        ssoCache.clear();
      }

      getLocationOrThrow().href = redirectLocation;
    },
    [
      isFaireDirectCreditForVisitorPhase2,
      signUpParams,
      modalVariant,
      faireDirectBrandToken,
      faireDirectBrand?.name,
      faireDirectBrandAlias,
      email.value,
      location.pathname,
      storeOpeningDate.value,
      recordModalSignUpActivity,
      saveIncompleteSignup,
      isActive,
    ]
  );

  const attemptSSOSignIn = useCallback(async () => {
    const { available_social_providers, missing_password } =
      await userLoginData(
        ILoginDataRequest.build({ email_address: email.value })
      );

    if ((available_social_providers?.length ?? 0) > 0 && showSSOButtons()) {
      setProviders(
        available_social_providers.map(
          (provider) => SocialSignOnProvider[provider]
        )
      );
      setPromptSSO(true);
      setUserHasPassword(!missing_password);
      trackSignupReportExistingSso();
    } else {
      trackSignupReportExistingEmail();
    }

    void trackEvent(`sign-up-page/modal/submit-email/in-use`);

    history.replace({
      ...history.location,
      search: qs.stringify({
        ...signUpParams,
        signUp: undefined,
        signIn: signUpParams.signUp ?? "1",
        email: email.value.trim(),
      }),
    });
  }, [
    email.value,
    setProviders,
    setPromptSSO,
    setUserHasPassword,
    signUpParams,
    history,
  ]);

  const handleSubmit = useCallback(
    async (
      e?: React.FormEvent,
      queryParamsOverride?: Partial<SignUpQueryParameters>
    ) => {
      e?.preventDefault();
      e?.stopPropagation();

      void trackEvent(`sign-up-page/modal/submit-email/attempt`);
      trackTimeBetweenEmailEntryAndSubmit();
      if (setEmailValidationErrors()) {
        void trackEvent(`sign-up-page/modal/submit-email/invalid`);
        return;
      }
      try {
        const isEmailAvailable = await getIsEmailAvailable();
        if (isEmailAvailable) {
          await signupWithAvailableEmail(queryParamsOverride);
        } else {
          await attemptSSOSignIn();
        }
      } catch (error) {
        setSomethingWentWrongEmailError(error);
      }
    },
    [
      trackTimeBetweenEmailEntryAndSubmit,
      setEmailValidationErrors,
      getIsEmailAvailable,
      signupWithAvailableEmail,
      attemptSSOSignIn,
      setSomethingWentWrongEmailError,
    ]
  );

  return {
    email,
    signUpParams,
    dismissible,
    modalVariant,
    isFaireDirectSignUp,
    storeOpeningDate,
    handleSubmit,
    handleModalClose,
    markUserEntersEmail,
    setEmailValidationErrors,
  };
};
